import React from 'react';
import { Avatar, Box, useMediaQuery } from '@mui/material';
import { FaOptionIcon, sendIcon } from '@europrocurement/l2d-icons';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import {
    SkeletonLoader,
    HeaderSkeleton,
    FlexyButton,
    useL2DTheme,
} from '@europrocurement/flexy-components';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { useParcoursFormalite } from '@europrocurement/l2d-modules/modules/ParcoursFormalite/provider';
import { CollectionDeDossierApiApiCollectionsDossiersIdValidatePutRequest } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import { HeaderTitle } from './fragments/HeaderTitle';
import { MainInfoSection } from './fragments/MainInfoSection';
import { PrescripteurOffreSection } from './fragments/PrescripteurOffreSection';
import { DOSSIER_STATUS_DRAFT } from '../../../constants/status';
import { DossierStatus } from '../../liste';

const themeColor = ['primary', 'secondary', 'warning', 'success', 'error'] as const;
type ThemeColor = (typeof themeColor)[number];
type HexColor = string;

export type DetailDossierTitleProps = {
    dossier: {
        statut: string;
        statutId: number;
        societeRef?: string;
        id: number;
        collectionId: number;
    };
    prescripteur: {
        id: number;
        raisonSociale: string;
        codeComptable: string;
        urlToDetails?: string;
    };
    offre: { libelle: string };
    clientOpere: { raisonSociale: string; siren?: string; urlToSocieteDotCom?: string; id: number };
    icon?: IconName;
    color?: ThemeColor | HexColor;
    source?: 'BACK' | 'FRONT';
    onEditSousClientSuccess?: () => void;
};

const DetailDossierTitle: React.FunctionComponent<DetailDossierTitleProps> = function ({
    dossier,
    prescripteur,
    offre,
    clientOpere,
    icon = 'folder',
    color = 'primary',
    source = 'FRONT',
    onEditSousClientSuccess = () => {},
}) {
    const snackbar = useSnackbar();
    const isThemeColor = (str: string): str is ThemeColor => themeColor.includes(str as ThemeColor);
    const { palette, breakpoints } = useL2DTheme();
    const { loading, setLoading } = useParcoursFormalite();
    const downMdBreakpoint = useMediaQuery(breakpoints.down('md'));
    const { collectionApi } = useFormaliteService();

    React.useEffect(() => {
        setLoading(false);
    }, [setLoading]);

    const avatarStyle = {
        display: 'flex',
        borderRadius: '8px',
        backgroundColor: palette.icon?.[isThemeColor(color) ? color : 'primary']?.backgroundColor,
        minHeight: '75px',
        minWidth: '75px',
    };

    if (downMdBreakpoint) {
        avatarStyle.display = 'none';
    }

    const validateDossier = async () => {
        try {
            const freshCollection = (
                await collectionApi.apiCollectionsDossiersIdGet({ id: `${dossier.collectionId}` })
            ).data;

            if (!freshCollection || !freshCollection.id) {
                throw new Error('missing freshCollection');
            }

            if (!freshCollection.client) {
                throw new Error('missing client');
            }
            if (!freshCollection.prescripteur) {
                throw new Error('missing prescripteur');
            }
            try {
                const request: CollectionDeDossierApiApiCollectionsDossiersIdValidatePutRequest = {
                    id: `${freshCollection?.id}`,
                    collectionDeDossierJsonldCollectiondossierValidated: {
                        prescripteur: freshCollection.prescripteur?.replace(
                            '/tiers/tiers',
                            '/formalite/tiers',
                        ),
                        urgent: freshCollection.urgent === true,
                        client: `/formalite/tiers/${freshCollection.client}`,
                        infoComp: freshCollection.infoComp,
                        libelle: freshCollection.libelle,
                    },
                };

                const res = await collectionApi.apiCollectionsDossiersIdValidatePut(request);

                return res;
            } catch (e) {
                snackbar.enqueueSnackbar((e as Error).message, { variant: 'error' });

                throw new Error((e as Error).message);
            }
        } catch (e) {
            snackbar.enqueueSnackbar((e as Error).message, { variant: 'error' });

            throw new Error((e as Error).message);
        }
    };

    const onSubmitHandler = () => {
        setLoading(true);
        validateDossier();
        setTimeout(() => {
            setLoading(false);
            snackbar.enqueueSnackbar('Dossier envoyé !');
            window.location.reload();
        }, 1500);
    };

    return (
        <SkeletonLoader
            isLoading={loading}
            skeleton={<HeaderSkeleton />}
            containerSx={{ marginBottom: '16px' }}
        >
            <Box sx={{ display: 'flex', gap: '0 24px' }}>
                <Avatar sx={avatarStyle}>
                    <FaOptionIcon
                        icon={['fasl', icon]}
                        color={color}
                    />
                </Avatar>
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            columnGap: '0.5em',
                        }}
                    >
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'start',
                                columnGap: '0.5em',
                            }}
                        >
                            {/* Titre avec bouton d'édition au hover */}
                            <HeaderTitle
                                clientOpereRaisonSociale={clientOpere.raisonSociale}
                                prescriberId={prescripteur?.id}
                                sousClientId={clientOpere.id}
                                onSuccess={onEditSousClientSuccess}
                            />

                            <Box sx={{ display: 'flex', gap: '0 8px', alignItems: 'center' }}>
                                <DossierStatus
                                    statutId={dossier.statutId}
                                    label={dossier.statut}
                                />
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'end',
                                columnGap: '0.5em',
                            }}
                        >
                            {DOSSIER_STATUS_DRAFT === dossier.statutId && (
                                <FlexyButton
                                    label="Envoyer au formaliste"
                                    color={palette.primary}
                                    variant="contained"
                                    onClick={onSubmitHandler}
                                    icon={sendIcon}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Informations complémentaires */}
                    <MainInfoSection
                        clientOpere={clientOpere}
                        dossier={dossier}
                        palette={palette}
                        // onUpdate={handleUpdateWithSkeleton}
                    />

                    {/* Prescripteur et offre */}
                    <PrescripteurOffreSection
                        prescripteur={prescripteur}
                        offre={offre}
                        palette={palette}
                        source={source}
                    />
                </Box>
            </Box>
        </SkeletonLoader>
    );
};

export default DetailDossierTitle;
